import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { PlatformUser } from '@nucleus-commerce/api-client/platform';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { Mono } from '@/shared/ui/mono';

import type { Actor } from '../schemas/user-rules';

import { UserRoleBadge } from './user-role-badge';
import { UserStatusBadge } from './user-status-badge';

/** Column model for the platform users table. Sortable by name and last sign-in only. */
export function useUserColumns({ actor }: { actor: Actor | null }) {
  const { t } = useTranslation('users');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<PlatformUser>>>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: t('columns.name'),
        meta: { sortField: 'name', lockVisibility: true },
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate font-medium">
              {row.original.name}
              {actor?.id === row.original.id ? (
                <span className="text-muted-foreground ml-1.5 text-xs font-normal">{t('you')}</span>
              ) : null}
            </span>
            <Mono className="truncate sm:hidden">{row.original.email}</Mono>
            {row.original.hasPassword ? null : (
              <span className="text-warning text-xs">{t('noPassword')}</span>
            )}
          </div>
        ),
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: t('columns.email'),
        enableSorting: false,
        cell: ({ row }) => <Mono className="truncate">{row.original.email}</Mono>,
      },
      {
        id: 'role',
        accessorKey: 'role',
        header: t('columns.role'),
        enableSorting: false,
        meta: { filterKey: 'role', searchable: false, tour: 'platform.users.roles' },
        cell: ({ row }) => <UserRoleBadge role={row.original.role} />,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('columns.status'),
        enableSorting: false,
        meta: { filterKey: 'status', searchable: false, tour: 'platform.users.status' },
        cell: ({ row }) => <UserStatusBadge status={row.original.status} />,
      },
      {
        id: 'lastLoginAt',
        accessorFn: (row) => row.lastLoginAt ?? undefined,
        header: t('columns.lastLoginAt'),
        sortUndefined: 'last',
        meta: { sortField: 'lastLoginAt', searchable: false },
        cell: ({ row }) =>
          row.original.lastLoginAt ? (
            <span className="text-muted-foreground" title={format.utc(row.original.lastLoginAt)}>
              {format.relative(row.original.lastLoginAt)}
            </span>
          ) : (
            <span className="text-muted-foreground">{t('neverSignedIn')}</span>
          ),
      },
    ],
    [actor, format, t],
  );
}
